import React from 'react';
import { useNavigate } from 'react-router-dom';
import { NonFdicNotice } from '../../components/NonFdicNotice';
import { HowItWorksSection, HowItWorksButton, ProcessCheckButton, BuyingEthButton, FeesButton, FormPurposesButton } from '../../components/pas/HowItWorks';
import { FollowUsButton } from '../../components/FollowUs';
import { ShareButtons } from '../../components/ShareButtons';
import { HeroTrailer, TrailerSection } from '../../components/pas/LandingTrailer';
import { BuyBethWidget } from '../../components/pas/BuyBethWidget';
import { MembershipTiers } from '../../components/pas/MembershipTiers';
import { RecentPurchases } from '../../components/pas/RecentPurchases';
import { HeroBackedBadge, HeroFeatureStrip } from '../../components/pas/HeroExtras';
import { usePASAdminAuth } from '../../context/PASAdminAuthContext';
import { 
  Wallet, CreditCard, ArrowRightLeft, FileCheck, Shield, 
  TrendingUp, Users, Banknote, ChevronRight, CheckCircle,
  Coins, Lock, Zap
} from 'lucide-react';

const BethcoinLanding = () => {
  const navigate = useNavigate();
  const { pasAdmin } = usePASAdminAuth();

  const features = [
    {
      icon: <Wallet className="w-8 h-8" />,
      title: "Open a BETH Wallet",
      description: "Create your digital wallet in seconds. Store, manage, and track your BETHCOIN balance."
    },
    {
      icon: <ArrowRightLeft className="w-8 h-8" />,
      title: "Barter & Exchange",
      description: "Use BETH as credit to barter or exchange debt obligations with any third party."
    },
    {
      icon: <Banknote className="w-8 h-8" />,
      title: "Pay Off Debt",
      description: "Leverage your BETH holdings to settle and pay off outstanding debts."
    },
    {
      icon: <FileCheck className="w-8 h-8" />,
      title: "You Cut The Check",
      description: "Create digital crypto checks with verifiable hash signatures. You are the banker now."
    }
  ];

  const benefits = [
    "1 BETH = $1.00 USD Exchange Rate",
    "Crypto Hash E-Signature on Every Check",
    "IRC Barter Credit Transaction Compliant",
    "1099-B Tax Reporting Ready",
    "Assignable to Any Third Party",
    "Instant Digital Transfers"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #10b981 0%, transparent 50%),
                              radial-gradient(circle at 75% 75%, #6366f1 0%, transparent 50%)`
          }}></div>
        </div>

        <nav className="relative z-10 w-full bg-[#141d2e] border-b border-emerald-500/20" data-testid="cover-banner-header">
          <div className="max-w-7xl mx-auto px-4 pt-4 flex justify-end">
            <div className="flex items-center gap-2 sm:gap-4 text-sm sm:text-base">
              <button
                onClick={() => navigate(pasAdmin ? '/pas/admin/dashboard' : '/pas/admin/login')}
                data-testid="header-admin-btn"
                className="px-4 py-2 border border-purple-500/50 text-purple-300 hover:bg-purple-500/10 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Shield className="w-4 h-4" /> Admin
              </button>
              <button
                onClick={() => navigate('/pas/login')}
                data-testid="header-sign-in-btn"
                className="px-4 py-2 text-white hover:text-emerald-400 transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/pas/signup')}
                className="px-6 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold transition-colors"
              >
                Open Wallet
              </button>
            </div>
          </div>
          <h1 className="max-w-7xl mx-auto px-4 pb-6 pt-2">
            <img src="/banner-strip.png" alt="BETHCOIN Exchange Market" data-testid="cover-banner-img"
              className="w-full max-w-5xl h-32 sm:h-48 lg:h-64 mx-auto object-contain" />
          </h1>
        </nav>

        <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 rounded-full text-emerald-400 text-sm mb-8">
            <Zap className="w-4 h-4" />
            <span data-testid="landing-stablecoin-badge">BETH USD Stablecoin · 1 BETH = $1.00 USD</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            YOU ARE THE<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
              BANKER NOW
            </span>
          </h1>
          
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10">
            Open a BETH Wallet. BETH USD is a dollar-for-dollar stablecoin, minted only against recorded cash and Treasury reserves.
            Use it as credit to barter or exchange debt obligations. Pay off debt. You cut the check.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <button
              onClick={() => navigate('/pas/signup')}
              data-testid="open-wallet-btn"
              className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-500/25"
            >
              <Wallet className="w-5 h-5" />
              Open Your BETH Wallet
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/pas/login')}
              className="px-8 py-4 border-2 border-slate-600 hover:border-emerald-500 text-white rounded-xl font-bold text-lg transition-all"
            >
              Access Existing Wallet
            </button>
            <a href="#buy" data-testid="hero-buy-btn" className="px-8 py-4 border-2 border-emerald-500/60 hover:bg-emerald-500/10 text-emerald-300 rounded-xl font-bold text-lg transition-all">
              Buy BETH USD
            </a>
          </div>
          <div className="flex justify-center mb-6"><HeroBackedBadge /></div>
          
          {/* Instruction Buttons Row */}
          <div className="flex flex-wrap gap-3 justify-center" data-testid="hero-instructions-row">
            <HowItWorksButton />
            <ProcessCheckButton />
            <BuyingEthButton />
            <FeesButton />
            <FormPurposesButton />
          </div>

          <HeroTrailer />
          <HeroFeatureStrip />

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto mt-16 pt-16 border-t border-slate-700">
            <div>
              <p className="text-2xl sm:text-3xl font-bold text-white">1:1</p>
              <p className="text-slate-400 text-sm">BETH to USD</p>
            </div>
            <div>
              <p className="text-2xl sm:text-3xl font-bold text-white">SHA-256</p>
              <p className="text-slate-400 text-sm">Crypto Signature</p>
            </div>
            <div>
              <p className="text-2xl sm:text-3xl font-bold text-white whitespace-nowrap">IRC §1031</p>
              <p className="text-slate-400 text-sm">Barter Compliant</p>
            </div>
          </div>
        </div>
      </header>

      <HowItWorksSection />

      {/* Features Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The Future of Credit Exchange
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              BETHCOIN combines traditional financial instruments with blockchain technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-6 hover:border-emerald-500/50 transition-all group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center text-emerald-400 mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-slate-800/30 overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Digital Crypto Checks with<br />
                <span className="text-emerald-400">Verifiable Signatures</span>
              </h2>
              <p className="text-slate-400 mb-8">
                Every check you create is secured with a SHA-256 cryptographic hash signature, 
                making it verifiable, tamper-proof, and legally assignable to any third party.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-white">{benefit}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate('/pas/signup')}
                className="mt-8 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
              >
                Start Creating Checks <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Sample Check Preview */}
            <div className="bg-white rounded-2xl p-6 shadow-2xl transform rotate-1 hover:rotate-0 transition-transform">
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6">
                {/* Check Header */}
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h4 className="text-lg font-bold text-slate-900">BETHCOIN EXCHANGE MARKET</h4>
                    <p className="text-xs text-slate-500">Digital Crypto Check</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-600">Check #BETH1234567890</p>
                    <p className="text-xs text-slate-500">Date: {new Date().toLocaleDateString()}</p>
                  </div>
                </div>

                {/* Pay To */}
                <div className="mb-4">
                  <p className="text-xs text-slate-500 mb-1">PAY TO THE ORDER OF</p>
                  <div className="border-b-2 border-slate-300 pb-1">
                    <p className="text-slate-800 font-medium">Sample Payee Name</p>
                  </div>
                </div>

                {/* Amount */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1">
                    <p className="text-xs text-slate-500 mb-1">AMOUNT IN WORDS</p>
                    <p className="text-sm text-slate-700">One Hundred and 00/100 Dollars</p>
                  </div>
                  <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg px-4 py-2">
                    <p className="text-lg font-bold text-emerald-700">$100.00</p>
                    <p className="text-xs text-emerald-600">1,000 BETH</p>
                  </div>
                </div>

                {/* Crypto Signature */}
                <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">CRYPTO SIGNATURE</p>
                    <p className="font-mono text-xs text-slate-700">SHA-256: A1B2C3D4E5F6G7H8</p>
                  </div>
                  <div className="flex items-center gap-1 text-emerald-600">
                    <Lock className="w-4 h-4" />
                    <span className="text-xs font-medium">VERIFIED</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrailerSection />

      <MembershipTiers />

      {/* Buy BETH USD */}
      <section id="buy" className="py-20 px-4" data-testid="landing-buy-section">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center [&>*]:min-w-0">
          <div className="space-y-4">
            <p className="text-emerald-400 text-xs uppercase tracking-[0.3em]">BETH USD stablecoin</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white">Buy BETH USD in seconds</h2>
            <p className="text-slate-300">A dollar-backed stablecoin on Base, backed by U.S. Treasury bills and cash. Pay with USDC or ETH from your own wallet; nothing is held by us.</p>
            <a href="/reserves" className="inline-block text-sm text-cyan-300 underline" data-testid="landing-buy-reserves-link">Check the live reserves</a>
          </div>
          <div className="flex flex-col items-center md:items-end gap-4 w-full min-w-0"><BuyBethWidget /><RecentPurchases /></div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 backdrop-blur border border-emerald-500/30 rounded-3xl p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Become Your Own Banker?
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              Open your BETH wallet today and start leveraging the power of crypto-backed 
              credit exchange for debt settlement and barter transactions.
            </p>
            <button
              onClick={() => navigate('/pas/signup')}
              className="px-10 py-4 bg-white text-slate-900 rounded-xl font-bold text-lg hover:bg-slate-100 transition-colors"
            >
              Open Free BETH Wallet
            </button>
          </div>
        </div>
      </section>

      {/* Other Services Section (admin only) */}
      {pasAdmin && (
      <section className="py-16 px-4 bg-slate-800/50" data-testid="admin-other-services">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-white mb-2">Restricted BETH Services</h2>
            <p className="text-slate-400">Visible to the BETHCOIN administrator only</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* BETH Finance - Consumer Loans */}
            <div 
              onClick={() => navigate('/beth-finance')}
              className="bg-slate-900/50 border border-slate-700 rounded-xl p-6 cursor-pointer hover:border-blue-500/50 hover:bg-slate-900/80 transition-all group"
            >
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                <CreditCard className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-400">BETH Finance</h3>
              <p className="text-slate-400 text-sm mb-4">Consumer credit enhancement, auto loans, and mortgage applications</p>
              <span className="text-blue-400 text-sm font-medium flex items-center gap-1">
                Apply Now <ChevronRight className="w-4 h-4" />
              </span>
            </div>

            {/* Admin Portal */}
            <div 
              onClick={() => navigate('/login')}
              className="bg-slate-900/50 border border-slate-700 rounded-xl p-6 cursor-pointer hover:border-purple-500/50 hover:bg-slate-900/80 transition-all group"
            >
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 group-hover:text-purple-400">Admin Portal</h3>
              <p className="text-slate-400 text-sm mb-4">Manage transactions, investors, and platform operations</p>
              <span className="text-purple-400 text-sm font-medium flex items-center gap-1">
                Admin Login <ChevronRight className="w-4 h-4" />
              </span>
            </div>

            {/* Investor Portal */}
            <div 
              onClick={() => navigate('/investors')}
              className="bg-slate-900/50 border border-slate-700 rounded-xl p-6 cursor-pointer hover:border-amber-500/50 hover:bg-slate-900/80 transition-all group"
            >
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 group-hover:text-amber-400">Investor Portal</h3>
              <p className="text-slate-400 text-sm mb-4">Private credit fund, tokenized securities, and DeFi investments</p>
              <span className="text-amber-400 text-sm font-medium flex items-center gap-1">
                Learn More <ChevronRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <NonFdicNotice className="mb-6" />
          
          {/* Share & Follow Us */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
            <ShareButtons url={window.location.origin + '/pas'} />
            <FollowUsButton />
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Coins className="w-6 h-6 text-emerald-400" />
              <span className="text-white font-bold">BETHCOIN EXCHANGE MARKET</span>
            </div>
            <p className="text-slate-500 text-sm">
              IRC §1031 / §6050W Barter Exchange Compliant · BETH USD on Base, 1:1 reserve-backed
            </p>
            <div className="flex gap-6 text-slate-400 text-sm">
              <a href="/terms" className="hover:text-white">Terms</a>
              <a href="/privacy" className="hover:text-white">Privacy</a>
              <a href="/compliance" className="hover:text-white">Compliance</a>
            </div>
          </div>
          
          {/* Quick Links */}
          <div className="border-t border-slate-800 pt-6 flex flex-wrap justify-center gap-4 text-sm" data-testid="footer-quick-links">
            <button onClick={() => navigate('/pas/login')} className="text-slate-400 hover:text-emerald-400">Client Login</button>
            <span className="text-slate-700">|</span>
            <button onClick={() => navigate('/reserves')} className="text-slate-400 hover:text-emerald-400" data-testid="footer-reserves-link">Proof of Reserves</button>
            <span className="text-slate-700">|</span>
            <a href="/#buy" className="text-slate-400 hover:text-emerald-400" data-testid="footer-buy-link">Buy BETH USD</a>
            <span className="text-slate-700">|</span>
            <a href="/pas/signup?next=buy-card" className="text-slate-400 hover:text-emerald-400" data-testid="footer-card-link">Buy by Card</a>
            <span className="text-slate-700">|</span>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/token-list/beth-usd.json`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-emerald-400" data-testid="footer-tokenlist-link">Token List</a>
            <span className="text-slate-700">|</span>
            <a href="#membership" className="text-slate-400 hover:text-amber-400" data-testid="footer-tiers-link">Tier 1 &amp; Tier 2 Membership</a>
            <span className="text-slate-700">|</span>
            <button onClick={() => navigate(pasAdmin ? '/pas/admin/dashboard' : '/pas/admin/login')} className="text-slate-400 hover:text-purple-400" data-testid="footer-admin-link">BETHCOIN Admin</button>
            {pasAdmin && (<>
              <span className="text-slate-700">|</span>
              <button onClick={() => navigate('/beth-finance')} className="text-slate-400 hover:text-blue-400">BETH Finance</button>
              <span className="text-slate-700">|</span>
              <button onClick={() => navigate('/login')} className="text-slate-400 hover:text-purple-400">Legacy Admin</button>
              <span className="text-slate-700">|</span>
              <button onClick={() => navigate('/investors')} className="text-slate-400 hover:text-amber-400">Investors</button>
            </>)}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BethcoinLanding;
